<template>
  <DefaultModal
    ref="modal"
    :title="mode === 'create_subtask' ? $t('create_subtask') : $t('switch_pool')"
    :submit-button-text="'confirm'"
    :saveButtonDisabled="!isValid"
    @submit="onSubmit"
    @modal-closed="handleClosed"
    eager
    class="move-to-lane-dialog"
  >
    <template v-slot:content>
      <v-form ref="formRef">
        <v-select
          v-model="selectedPoolId"
          :items="pools"
          item-text="pool_title"
          item-value="pool_id"
          :label="$t('select_pool')"
          :rules="[v => !!v || $t('form_rules.mandatory')]"
          outlined
          @change="onPoolChange"
        />

        <v-select
          v-model="selectedStatusId"
          :items="availableStatuses"
          item-text="status_title"
          item-value="status_id"
          :label="$t('define_status')"
          :rules="[v => !!v || $t('form_rules.mandatory')]"
          :disabled="!selectedPoolId"
          outlined
          @change="onStatusChange"
        />

        <v-select
          v-model="selectedLaneId"
          :items="availableLanes"
          item-text="title"
          item-value="lane_id"
          :label="$t('select_lane')"
          :rules="[v => !!v || $t('form_rules.mandatory')]"
          :disabled="!selectedPoolId"
          outlined
        />

        <v-text-field
          v-if="isExecutorInSelectedLane"
          v-model.number="slaHours"
          :label="$t('custom_sla_hours')"
          type="number"
          min="0"
          step="1"
          dense
          outlined
          hide-details
          suffix="h"
        />

        <v-checkbox
          v-if="currentJobPoolStatus?.allow_sum_sla"
          v-model="sumSLA"
          :label="$t('sum_sla_label')"
          hide-details
        />
      </v-form>
    </template>
  </DefaultModal>
</template>

<script>
import { mapActions } from 'vuex'

import CommentAPI from '@/services/api/job/comment'
import JobAPI from '@/services/api/job/job'
import JobPoolStatusAPI from '@/services/api/job/job_pool_status'
import NextStatusAPI from '@/services/api/job/next_status'
import JobHistoryAPI from '@/services/api/job/job_history'

import DefaultModal from '@/components/DefaultModal.vue'
import JobActions from '@/store/job/actions-types'

export default {
  name: 'MoveToLaneDialog',

  components: { DefaultModal },

  props: {
    value: { type: Boolean, default: false },
    pools: { type: Array, default: () => [] },
    statuses: { type: Array, default: () => [] },
    lanes: { type: Array, default: () => [] },
    userLanes: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    job: { type: Object, default: () => ({}) },
    initialSelection: {
      type: Object,
      default: () => ({ poolId: null, statusId: null, laneId: null, slaHours: null, sumSLA: false })
    },
    mode: {
      type: String,
      default: 'move', // 'move' or 'create_subtask'
      validator: (value) => ['move', 'create_subtask'].includes(value)
    }
  },

  data() {
    return {
      selectedJobId: null,
      selectedPoolId: null,
      selectedStatusId: null,
      originalLaneId: null,
      selectedLaneId: null,
      slaHours: null,
      sumSLA: false,
      originalStatusId: null,
      lockedPool: false,
      jobDefinitionId: null,
      jobDefinitionStatus: [],
      nextStatus: [],
      jobHistory: null,
    }
  },

  computed: {
    isValid() {
      const hasPool = !!this.selectedPoolId
      const hasStatus = !!this.selectedStatusId
      const hasLane = !!this.selectedLaneId

      return hasPool && hasStatus && hasLane
    },

    currentPoolId() {
      return this.lanes.find(l => l.lane_id == this.originalLaneId)?.pool_id
    },

    currentJobPoolStatus() {
      return this.jobDefinitionStatus.find(jps => jps.pool_id == this.currentPoolId && jps.status_id == this.originalStatusId)
    },

    selectedPoolComputed() {
      const list = this.pools || []
      return list.find(p => (p.pool_id || p?.raw?.pool_id) === this.selectedPoolId)
        || list.find(p => p?.raw?.pool_id === this.selectedPoolId)
        || null
    },

    availableStatuses() {
      const pool = (this.selectedPoolComputed && (this.selectedPoolComputed.raw || this.selectedPoolComputed)) || null
      if (!pool || ! this.currentJobPoolStatus) return []

      let poolStatusesIds = this.jobDefinitionStatus
        .filter(jps => jps.pool_id == pool.pool_id)
        .map(jps => jps.status_id)

      const possibleNextStatus = this.nextStatus
      .filter(ns => ns.source_job_pool_status_id == this.currentJobPoolStatus?.job_pool_status_id)
      .map(ns => ns.next_job_pool_status_id)

      if (possibleNextStatus?.length > 0) {
        let allowedJobPoolStatus = this.jobDefinitionStatus.filter(
          jds => jds.pool_id == pool.pool_id &&
          (
            possibleNextStatus.includes(jds.job_pool_status_id)
            || (jds.job_pool_status_id == this.currentJobPoolStatus?.job_pool_status_id)
          )
        )

        poolStatusesIds = allowedJobPoolStatus.map(jps => jps.status_id)
      }

      return (this.statuses || []).filter(s => poolStatusesIds.includes(s.status_id))
    },

    availableLanes() {
      if (!this.selectedPoolId) return []
      const lanes = (this.lanes || []).filter(l => (l.pool && (l.pool.pool_id === this.selectedPoolId)))
      return lanes.map(l => ({ lane_id: l.lane_id || l.id, title: l.lane_title }))
    },

    isExecutorInSelectedLane() {
      return this.userLanes.includes(this.selectedLaneId)
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initializeSelection()
          this.openModal()

          this.$emit('opened')
        } else {
          if (this.$refs.modal) this.$refs.modal.closeModal()
        }
      }
    }
  },

  methods: {
    ...mapActions('job', [
      JobActions.GET_JOB,
    ]),

    initializeSelection() {
      const sel = this.initialSelection || {}

      this.selectedJobId = sel.jobId
      this.selectedPoolId = sel.poolId || null
      this.originalStatusId = sel.originalStatusId || null
      this.selectedStatusId = sel.statusId || null
      this.selectedLaneId = sel.laneId || null
      this.originalLaneId = sel.originalLaneId || null
      this.slaHours = sel.slaHours || null
      this.sumSLA = !!sel.sumSLA
      this.jobDefinitionId = sel.jobDefinitionId || null
      this.jobHistory = null
    },

    onPoolChange() {
      this.selectedStatusId = null
      this.slaHours = null
      const lanes = this.availableLanes
      if (lanes.length === 1) {
        this.selectedLaneId = lanes[0].lane_id
      } else {
        const lane = this.getMoveToLane()
        this.selectedLaneId = lane?.lane_id || lane?.id || null
      }
    },

    onStatusChange() {
      // Find the job pool status for the selected pool and status
      // to apply its default SLA value to the custom SLA field.
      const jobPoolStatus = this.jobDefinitionStatus.find(
        jps => jps.pool_id == this.selectedPoolId && jps.status_id == this.selectedStatusId
      )

      this.slaHours = jobPoolStatus?.sla ?? null
    },


    async openModal(pool) {
      this.$emit('opened')

      pool = pool ?? this.selectedPoolId

      // Fetch current job data to check status_id
      if (this.job?.job_id) {
        try {
          const response = await JobAPI.getJob(this.job.job_id)
          const currentJob = response.data

          this.selectedJobId = currentJob.job_id
          this.jobDefinitionId = currentJob.job_definition_id || null
          this.originalStatusId = currentJob.status_id || null
          this.originalLaneId = currentJob.lane_id || null
        } catch (error) {
          console.error('Error fetching job:', error)
        }
      }

      const historyParams = {
        order: 'job_history_id',
        direction: 'DESC',
        offset: 0,
        limit: 9999
      }

      const jobHistory = await JobHistoryAPI.getHistoryByJob(this.selectedJobId, historyParams)
      this.jobHistory = [...jobHistory.data.items || []]

      await JobPoolStatusAPI.getJobPoolStatusByJobDefinition(this.jobDefinitionId)
        .then(async (result) => {
          const jobPoolStatuses = result.data.items || []
          this.jobDefinitionStatus = [...jobPoolStatuses]

          await jobPoolStatuses.forEach(async (jobPoolStatus) => {
            const jobNextStatus = await NextStatusAPI.getByCurrentPoolStatus(jobPoolStatus.job_pool_status_id)

            this.nextStatus = [...this.nextStatus, ...jobNextStatus.data.items]
          })
        })

      if (pool) {
        this.selectedPoolId = pool
        const lane = this.getMoveToLane(this.selectedLaneId || null)
        this.selectedLaneId = lane?.lane_id || lane?.id || lane
        this.selectedStatusId = null
        this.slaHours = null
        this.sumSLA = false
      } else {
        // Use provided initialSelection when available
        const sel = this.initialSelection || {}
        this.selectedPoolId = sel.poolId || null
        const lane = this.getMoveToLane(sel.laneId || null)
        this.selectedLaneId = lane?.lane_id || sel.laneId || lane
        this.selectedStatusId = sel.statusId || null
        this.slaHours = sel.slaHours || null
        this.sumSLA = !!sel.sumSLA
      }

      this.$nextTick(() => this.$refs.modal && this.$refs.modal.openModal(pool))
    },

    closeModal() {
      if (this.$refs.modal) this.$refs.modal.closeModal()
      this.handleClosed()
    },

    onSubmit() {
      const innerValid = this.$refs.formRef ? this.$refs.formRef.validate() : true

      if (!this.isValid || !innerValid) {
        this.$refs.modal && this.$refs.modal.setIsSubmitting(false)
        return
      }

      const confirmPayload = {
        poolId: this.selectedPoolId,
        statusId: this.selectedStatusId,
        laneId: this.selectedLaneId,
        slaHours: this.slaHours,
        sumSLA: !!this.sumSLA,
      }

      this.$emit('confirm', confirmPayload)

      // Check if status has changed and create comment (only in move mode)
      if (this.mode === 'move' && this.originalStatusId !== this.selectedStatusId && this.job?.job_id) {
        try {
          const newStatus = this.statuses.find(s => s.status_id === this.selectedStatusId)
          const newStatusTitle = newStatus?.status_title || ''

          const commentData = {
            job_id: parseInt(this.job.job_id),
            message: `O <b>status</b> foi alterado para <b>${newStatusTitle}</b>`,
            comment_type: 2
          }

          CommentAPI.createComment(commentData)
        } catch (error) {
          console.error('Error creating status change comment:', error)
        }
      }

      // Emit legacy shape for @submit listeners
      const poolObj = (this.pools || []).find(p => p.pool_id === this.selectedPoolId) || null
      const laneObj = (this.lanes || []).find(l => (l.lane_id || l.id) === this.selectedLaneId) || null
      const legacyPayload = {
        pool: poolObj,
        toLane: laneObj,
        status_id: this.selectedStatusId,
        sumSLA: !!this.sumSLA,
      }
      this.$emit('submit', legacyPayload)

      this.$refs.modal && this.$refs.modal.setIsSubmitting(false)
      this.$refs.modal && this.$refs.modal.closeModal()
    },

    handleClosed() {
      this.$emit('input', false)
      this.$emit('closed')
    },

    async submit() {
      if (! this.$refs.form.validate()) {
        return
      }

      const moveData = {
        pool: this.pool,
        toLane: this.toLane,
        status_id: this.newStatusId,
        sla: this.sumSLA || false,
      }

      this.$emit('submit', moveData)

      this.closeModal()
    },

    getMoveToLane(lane = null) {
      const pool = (this.pools || []).find(p => p.pool_id === this.selectedPoolId)
      if (!pool) return lane

      const poolHistory = this.jobHistory.filter(jh => jh.pool_id == this.selectedPoolId)
      if (poolHistory.length == 0) {
        return lane
      }

      const lastLaneId = poolHistory[0].lane_id

      return (this.lanes || []).find(l => l.lane_id == lastLaneId) ?? lane
    },
  }
}
</script>

<style scoped>
.move-to-lane-dialog >>> .v-dialog {
  z-index: 10002;
}
</style>
